import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { canAssignClients } from '../lib/roles';
import { formatCurrency, formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, 
  Search, 
  Building2, 
  Phone, 
  Mail,
  MapPin,
  MoreVertical,
  User,
  UserCheck,
  Trash2,
  Edit,
  Globe,
  Lock,
  PlusCircle
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useFeatures } from '../lib/features';
import ClientsDirectory from '../components/clients/ClientsDirectory';

const AdvertisersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { salesOnly, clientsLabel, clientLabel } = useFeatures();
  const isSalesRep = user?.role === 'sales_rep';
  // Only admins, accounting and sales admins choose or change a client's sales rep.
  const canAssign = canAssignClients(user);
  const myRepId = user?.matched_sales_rep_id || null;
  const canWriteAdvertiser = (adv) => {
    if (!isSalesRep) return true;
    const assigned = adv?.assigned_sales_rep_id;
    if (!assigned) return true; // unassigned → editable
    return assigned === myRepId;
  };
  const [advertisers, setAdvertisers] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [salesRepFilter, setSalesRepFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedAdvertiser, setSelectedAdvertiser] = useState(null);
  const [directoryRefresh, setDirectoryRefresh] = useState(0);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when the property type becomes known
  }, [salesOnly]);

  const loadData = async () => {
    try {
      // A sales-team property can hold thousands of clients: its table asks
      // the server for one page at a time, so the full list is not loaded.
      const [advertisersData, industriesData, salesRepsData] = await Promise.all([
        salesOnly ? Promise.resolve([]) : api.get('/advertisers'),
        api.get('/industries'),
        api.get('/sales-reps'),
      ]);
      setAdvertisers(advertisersData);
      setIndustries(industriesData);
      setSalesReps(salesRepsData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const salesRepNameById = React.useMemo(
    () => Object.fromEntries(salesReps.map(r => [r.id, r.name])),
    [salesReps]
  );

  const filteredAdvertisers = advertisers.filter(adv => {
    const matchesSearch =
      adv.business_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      adv.industry?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRep =
      salesRepFilter === 'all'
        ? true
        : salesRepFilter === 'unassigned'
          ? !adv.assigned_sales_rep_id
          : adv.assigned_sales_rep_id === salesRepFilter;
    return matchesSearch && matchesRep;
  }).sort((a, b) => (a.business_name || '').localeCompare(b.business_name || ''));

  const advertiserModal = (
    <AdvertiserModal
      isOpen={showCreateModal}
      onClose={() => {
        setShowCreateModal(false);
        setSelectedAdvertiser(null);
      }}
      advertiser={selectedAdvertiser}
      industries={industries}
      salesReps={salesReps}
      salesOnly={salesOnly}
      clientLabel={clientLabel}
      isSalesRep={isSalesRep}
      canAssign={canAssign}
      onSave={() => {
        loadData();
        setDirectoryRefresh(n => n + 1);
        setShowCreateModal(false);
        setSelectedAdvertiser(null);
      }}
      onIndustryAdded={loadData}
    />
  );

  if (salesOnly) {
    return (
      <div data-testid="advertisers-page">
        <ClientsDirectory
          salesReps={salesReps}
          refreshKey={directoryRefresh}
          onNew={() => { setSelectedAdvertiser(null); setShowCreateModal(true); }}
          onEdit={(c) => { setSelectedAdvertiser(c); setShowCreateModal(true); }}
        />
        {advertiserModal}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="advertisers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#F8FAFC] tracking-tight">{clientsLabel}</h1>
          <p className="text-[#94A3B8] mt-1">Manage your {clientLabel.toLowerCase()} accounts and contacts</p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)}
          data-testid="create-advertiser-btn"
          className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          New {clientLabel}
        </Button>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <Input
            data-testid="advertiser-search"
            placeholder="Search advertisers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#121212] border-[#1E293B] text-[#F8FAFC] placeholder:text-[#475569]"
          />
        </div>
        <div className="w-full md:w-64">
          <Select value={salesRepFilter} onValueChange={setSalesRepFilter}>
            <SelectTrigger
              className="bg-[#121212] border-[#1E293B] text-[#F8FAFC]"
              data-testid="advertiser-sales-rep-filter"
            >
              <SelectValue placeholder="Filter by Sales Rep" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-[#1E293B]">
              <SelectItem value="all" className="text-[#F8FAFC]">All Sales Reps</SelectItem>
              <SelectItem value="unassigned" className="text-[#F8FAFC]">Unassigned</SelectItem>
              {[...salesReps].sort((a, b) => a.name.localeCompare(b.name)).map(r => (
                <SelectItem key={r.id} value={r.id} className="text-[#F8FAFC]">
                  {r.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Advertisers Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3B82F6]"></div>
        </div>
      ) : filteredAdvertisers.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAdvertisers.map((advertiser, idx) => {
            const writable = canWriteAdvertiser(advertiser);
            const assignedRepName = advertiser.assigned_sales_rep_id
              ? salesRepNameById[advertiser.assigned_sales_rep_id]
              : null;
            return (
            <div 
              key={advertiser.id}
              role="link"
              tabIndex={0}
              onClick={() => navigate(`/advertisers/${advertiser.id}`)}
              onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) navigate(`/advertisers/${advertiser.id}`); }}
              className={`bg-[#121212] border rounded-md p-4 transition-colors animate-fadeInUp cursor-pointer focus:outline-none focus-visible:border-[#3B82F6] ${
                writable ? 'border-[#1E293B] hover:border-[#3B82F6]/50' : 'border-[#1E293B]/60 opacity-90 hover:border-[#3B82F6]/30'
              }`}
              style={{ animationDelay: `${idx * 50}ms` }}
              data-testid={`advertiser-card-${advertiser.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-[#1E293B] flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-[#3B82F6]" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-[#F8FAFC]">{advertiser.business_name}</h3>
                      {isSalesRep && !writable && (
                        <span
                          className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded bg-amber-900/30 text-amber-300 border border-amber-800"
                          title={`Assigned to ${assignedRepName || 'another sales rep'} — read-only for you`}
                          data-testid={`advertiser-readonly-${advertiser.id}`}
                        >
                          <Lock className="w-3 h-3" /> Read-only
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#94A3B8]">{advertiser.industry || 'No industry'}</p>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`advertiser-menu-${advertiser.id}`}>
                      <MoreVertical className="h-4 w-4 text-[#94A3B8]" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-[#121212] border-[#1E293B]" onClick={(e) => e.stopPropagation()}>
                    {writable ? (
                      <>
                        <DropdownMenuItem 
                          onClick={() => {
                            setSelectedAdvertiser(advertiser);
                            setShowCreateModal(true);
                          }}
                          className="text-[#F8FAFC] focus:bg-[#1E293B]"
                        >
                          <Edit className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-red-400 focus:bg-[#1E293B]"
                          onClick={async () => {
                            if (window.confirm('Delete this advertiser?')) {
                              await api.delete(`/advertisers/${advertiser.id}`);
                              loadData();
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <DropdownMenuItem
                        disabled
                        className="text-[#475569] focus:bg-transparent"
                        data-testid={`advertiser-readonly-menuitem-${advertiser.id}`}
                      >
                        <Lock className="w-4 h-4 mr-2" /> Assigned to {assignedRepName || 'another rep'}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="space-y-2 text-sm">
                {advertiser.address && (
                  <div className="flex items-center gap-2 text-[#94A3B8]">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{advertiser.address}</span>
                  </div>
                )}
                {advertiser.phone && (
                  <div className="flex items-center gap-2 text-[#94A3B8]">
                    <Phone className="w-4 h-4 flex-shrink-0" />
                    <span>{advertiser.phone}</span>
                  </div>
                )}
                {advertiser.email && (
                  <div className="flex items-center gap-2 text-[#94A3B8]">
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{advertiser.email}</span>
                  </div>
                )}
                {advertiser.website && (
                  <div className="flex items-center gap-2 text-[#94A3B8]">
                    <Globe className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{advertiser.website}</span>
                  </div>
                )}
                {advertiser.contact_name && (
                  <div className="flex items-center gap-2 text-[#94A3B8] mt-2 pt-2 border-t border-[#1E293B]">
                    <User className="w-4 h-4 flex-shrink-0" />
                    <span>{advertiser.contact_name}</span>
                  </div>
                )}
                {advertiser.assigned_sales_rep_id && salesRepNameById[advertiser.assigned_sales_rep_id] && (
                  <div
                    className="flex items-center gap-2 text-[#3B82F6] text-xs pt-1"
                    data-testid={`advertiser-assigned-rep-${advertiser.id}`}
                  >
                    <UserCheck className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">Sales Rep: {salesRepNameById[advertiser.assigned_sales_rep_id]}</span>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-[#1E293B] flex items-center justify-between">
                <span className="text-xs text-[#94A3B8]">
                  {advertiser.industry || 'Uncategorized'}
                </span>
                <div className="text-right">
                  <p className="text-sm font-medium text-[#F8FAFC]">{formatCurrency(advertiser.total_spend || 0)}</p>
                  <p className="text-xs text-[#94A3B8]">{advertiser.booking_count || 0} bookings</p>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-64 text-[#94A3B8]">
          <Building2 className="w-16 h-16 mb-4 opacity-50" />
          <p className="text-lg">No advertisers found</p>
          <Button 
            onClick={() => setShowCreateModal(true)} 
            variant="outline" 
            className="mt-4 border-[#1E293B] text-[#94A3B8] hover:bg-[#1E293B]"
          >
            Create your first advertiser
          </Button>
        </div>
      )}

      {/* Create/Edit Advertiser Modal */}
      {advertiserModal}
    </div>
  );
};

// Advertiser Modal Component
const EXTRA_FIELDS = { alt_phone: '', postal_code: '', location_type: '', docket_numbers: '', source: '', contract_end_date: '' };

export const AdvertiserModal = ({
  isOpen, onClose, advertiser, industries, salesReps = [], onSave, onIndustryAdded,
  salesOnly = false, clientLabel = 'Advertiser', isSalesRep = false, canAssign = false,
}) => {
  const [saveError, setSaveError] = useState('');
  const [formData, setFormData] = useState({
    ...EXTRA_FIELDS,
    business_name: '',
    industry: '',
    address: '',
    phone: '',
    website: '',
    email: '',
    email_cc1: '',
    email_cc2: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    notes: '',
    assigned_sales_rep_id: ''
  });
  const [saving, setSaving] = useState(false);
  const [showNewIndustry, setShowNewIndustry] = useState(false);
  const [newIndustryName, setNewIndustryName] = useState('');
  const [addingIndustry, setAddingIndustry] = useState(false);

  useEffect(() => {
    if (advertiser) {
      setFormData({
        business_name: advertiser.business_name || '',
        industry: advertiser.industry || '',
        address: advertiser.address || '',
        phone: advertiser.phone || '',
        website: advertiser.website || '',
        email: advertiser.email || '',
        email_cc1: advertiser.email_cc1 || '',
        email_cc2: advertiser.email_cc2 || '',
        contact_name: advertiser.contact_name || '',
        contact_phone: advertiser.contact_phone || '',
        contact_email: advertiser.contact_email || '',
        notes: advertiser.notes || '',
        assigned_sales_rep_id: advertiser.assigned_sales_rep_id || '',
        alt_phone: advertiser.alt_phone || '',
        postal_code: advertiser.postal_code || '',
        location_type: advertiser.location_type || '',
        docket_numbers: advertiser.docket_numbers || '',
        contract_end_date: advertiser.contract_end_date || '',
        source: advertiser.source || '',
      });
    } else {
      setFormData({
        ...EXTRA_FIELDS,
        business_name: '',
        industry: '',
        address: '',
        phone: '',
        website: '',
        email: '',
        email_cc1: '',
        email_cc2: '',
        contact_name: '',
        contact_phone: '',
        contact_email: '',
        notes: '',
        assigned_sales_rep_id: ''
      });
    }
    setSaveError('');
  }, [advertiser, isOpen]);

  const handleAddIndustry = async () => {
    if (!newIndustryName.trim()) return;
    setAddingIndustry(true);
    try {
      const result = await api.post('/industries', { name: newIndustryName.trim() });
      setFormData({ ...formData, industry: result.name });
      setNewIndustryName('');
      setShowNewIndustry(false);
      if (onIndustryAdded) onIndustryAdded();
    } catch (error) {
      console.error('Failed to add industry:', error);
    } finally {
      setAddingIndustry(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        assigned_sales_rep_id:
          formData.assigned_sales_rep_id && formData.assigned_sales_rep_id !== '__unassigned'
            ? formData.assigned_sales_rep_id
            : null,
      };
      if (advertiser) {
        await api.put(`/advertisers/${advertiser.id}`, payload);
      } else {
        await api.post('/advertisers', payload);
      }
      onSave();
    } catch (error) {
      console.error('Failed to save advertiser:', error);
      const detail = error.response?.data?.detail;
      setSaveError(typeof detail === 'string' ? detail : 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{advertiser ? `Edit ${clientLabel}` : `New ${clientLabel}`}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Section 1: Business Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-[#94A3B8] uppercase tracking-wide border-b border-[#1E293B] pb-2">
              Business Information
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Business Name *</Label>
                <Input
                  data-testid="advertiser-name-input"
                  value={formData.business_name}
                  onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                  required
                  className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Industry</Label>
                {showNewIndustry ? (
                  <div className="flex gap-2">
                    <Input
                      data-testid="new-industry-input"
                      value={newIndustryName}
                      onChange={(e) => setNewIndustryName(e.target.value)}
                      placeholder="Enter new industry"
                      className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                    />
                    <Button 
                      type="button" 
                      onClick={handleAddIndustry}
                      disabled={addingIndustry}
                      className="bg-[#3B82F6] hover:bg-[#2563EB] text-white px-3"
                    >
                      {addingIndustry ? '...' : 'Add'}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => {
                        setShowNewIndustry(false);
                        setNewIndustryName('');
                      }}
                      className="border-[#1E293B] text-[#94A3B8] px-3"
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Select
                      value={formData.industry}
                      onValueChange={(value) => setFormData({ ...formData, industry: value })}
                    >
                      <SelectTrigger className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] flex-1" data-testid="advertiser-industry-select">
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#1E293B] max-h-[200px]">
                        {[...industries].sort((a, b) => a.name.localeCompare(b.name)).map(ind => (
                          <SelectItem key={ind.id} value={ind.name} className="text-[#F8FAFC]">
                            {ind.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => setShowNewIndustry(true)}
                      className="border-[#1E293B] text-[#94A3B8] px-3"
                      data-testid="add-industry-btn"
                    >
                      <PlusCircle className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Address</Label>
              <Input
                data-testid="advertiser-address-input"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Phone</Label>
                <Input
                  data-testid="advertiser-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Website</Label>
                <Input
                  data-testid="advertiser-website-input"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://"
                  className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">E-mail</Label>
                <Input
                  data-testid="advertiser-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                />
              </div>
            </div>
            {salesOnly && (
              <div className="grid grid-cols-3 gap-4" data-testid="client-extra-fields">
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">Alt Phone</Label>
                  <Input
                    data-testid="advertiser-alt-phone-input"
                    value={formData.alt_phone}
                    onChange={(e) => setFormData({ ...formData, alt_phone: e.target.value })}
                    className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">Postal Code</Label>
                  <Input
                    data-testid="advertiser-postal-input"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value.toUpperCase() })}
                    placeholder="T4N 1X5"
                    className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">Docket Numbers</Label>
                  <Input
                    data-testid="advertiser-dockets-input"
                    value={formData.docket_numbers}
                    onChange={(e) => setFormData({ ...formData, docket_numbers: e.target.value })}
                    placeholder="RD15640, RD15702"
                    className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                  />
                </div>
              </div>
            )}
            {salesOnly && canAssign && (
              <div className="grid grid-cols-3 gap-4 items-end" data-testid="client-contract-field">
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">Contract End Date</Label>
                  <Input
                    data-testid="advertiser-contract-end-input"
                    type="date"
                    value={formData.contract_end_date}
                    onChange={(e) => setFormData({ ...formData, contract_end_date: e.target.value })}
                    className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                  />
                </div>
                <p className="col-span-2 text-xs text-[#94A3B8] pb-2">Not released to the pool before this date. The inactivity clock starts when the contract ends.</p>
              </div>
            )}
            {!salesOnly && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Secondary E-mail (CC)</Label>
                <Input
                  data-testid="advertiser-email-cc1-input"
                  type="email"
                  value={formData.email_cc1}
                  onChange={(e) => setFormData({ ...formData, email_cc1: e.target.value })}
                  placeholder="Also receives invoices"
                  className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Tertiary E-mail (CC)</Label>
                <Input
                  data-testid="advertiser-email-cc2-input"
                  type="email"
                  value={formData.email_cc2}
                  onChange={(e) => setFormData({ ...formData, email_cc2: e.target.value })}
                  placeholder="Also receives invoices"
                  className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                />
              </div>
            </div>
            )}
          </div>

          {/* Section 2: Contact Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-[#94A3B8] uppercase tracking-wide border-b border-[#1E293B] pb-2">
              Primary Contact
            </h3>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Contact Name</Label>
                <Input
                  data-testid="contact-name-input"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Contact Phone</Label>
                <Input
                  data-testid="contact-phone-input"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Contact Email</Label>
                <Input
                  data-testid="contact-email-input"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                />
              </div>
            </div>
          </div>

          {salesOnly && (
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Notes</Label>
              <textarea
                data-testid="advertiser-notes-input"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full rounded-md bg-[#0A0A0A] border border-[#1E293B] text-[#F8FAFC] text-sm p-2"
              />
            </div>
          )}

          {/* Section 3: Assignment (admins and accounting only) */}
          {canAssign && (
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-[#94A3B8] uppercase tracking-wide border-b border-[#1E293B] pb-2">
              Assignment
            </h3>
            <div className="space-y-2 max-w-md">
              <Label className="text-[#94A3B8]">Assigned Sales Rep</Label>
              <Select
                value={formData.assigned_sales_rep_id || '__unassigned'}
                onValueChange={(value) =>
                  setFormData({ ...formData, assigned_sales_rep_id: value })
                }
              >
                <SelectTrigger
                  className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                  data-testid="advertiser-sales-rep-select"
                >
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#1E293B] max-h-[200px]">
                  <SelectItem value="__unassigned" className="text-[#94A3B8]">
                    Unassigned
                  </SelectItem>
                  {[...salesReps].sort((a, b) => a.name.localeCompare(b.name)).map(rep => (
                    <SelectItem
                      key={rep.id}
                      value={rep.id}
                      className="text-[#F8FAFC]"
                      data-testid={`advertiser-sales-rep-option-${rep.id}`}
                    >
                      {rep.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#475569]">
                {salesOnly
                  ? 'Sales reps work the clients assigned to them. Unassigned clients are available for any rep to claim.'
                  : 'Sales reps only see advertisers and bookings assigned to them.'}
              </p>
            </div>
          </div>
          )}

          {saveError && (
            <div className="text-sm text-red-400 bg-red-900/20 border border-red-800 rounded-md p-2" data-testid="advertiser-save-error">{saveError}</div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-[#1E293B]">
            <Button type="button" variant="outline" onClick={onClose} className="border-[#1E293B] text-[#94A3B8]">
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={saving}
              data-testid="save-advertiser-btn"
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
            >
              {saving ? 'Saving...' : (advertiser ? 'Update' : 'Create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdvertisersPage;
